import {
  breakpointSizes,
  ButtonDesigns,
  ButtonTypes,
  getBreakpointDown,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import _ from 'lodash'
import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import Box from 'src/components/Box/Box'
import Button from 'src/components/Button/Button'
import CreditCardDropdown, {
  CreditCardDropdownInfiniteScrollProps,
  creditCardOption,
} from 'src/components/CreditCardDropdown/CreditCardDropdown'
import EmptyDropdownMessage from 'src/components/EmptyDropdownMessage/EmptyDropdownMessage'
import { ExpenseCategoriesContext } from 'src/components/ExpenseCategoriesInputBox/ExpenseCategoriesInputBox'
import ExpenseCategoriesInputs from 'src/components/ExpenseCategoriesInputs/ExpenseCategoriesInputs'
import SvgArrowCircle from 'src/components/Svgs/SvgArrowCircle'
import TextDropdown from 'src/components/TextDropdown/TextDropdown'
import TextInputField, {
  TextInputFieldDesigns,
} from 'src/components/TextInputField/TextInputField'
import Tooltip from 'src/components/Tooltip/Tooltip'
import { svgImageNames } from 'src/enums'
import {
  FormFieldsValidation,
  useFormFields,
  useKeyedDivRefs,
  useScrollToScrollGroupRefs,
} from 'src/hooks'
import { useColorOrGrayscaleSVG } from 'src/hooks/brandHooks'
import { translateSingleString, useTranslate } from 'src/localization/Trns'
import {
  AutomationComparator,
  AutomationRuleDetailModel,
  AutomationRuleObjectType,
  AutomationRuleStatusType,
} from 'src/models/automationRules.models'
import { ExpenseDetailsModel } from 'src/models/transaction.models'
import { useQueryCreditCards } from 'src/queries/creditCard.queries'
import { useQueryMccGroups } from 'src/queries/mccGroups.queries'
import { PutAutomationRuleRequest } from 'src/queries/organizations/automationRules.queries'
import { useQueryDepartments } from 'src/queries/organizations/department.queries'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { selectOrganizationIdFromRoute } from 'src/redux/selectors/data/organizations.selectors'
import { BackendErrorPayload, parseBackendErrors } from 'src/utils/error.utils'
import {
  stringIsEmpty,
  stringLengthIsLessThan,
} from 'src/utils/validation/string.validation'
import { css } from 'styled-components'

// enum for mapping scroll groups, order matches DOM order
enum AutomationRuleFormInputBoxes {
  NameInputBox,
  RuleDefinitionInputBox,
}

type AutomationRuleFormFields = {
  name?: string
  fundingSourceId?: string
  merchantName?: string
  extendMccGroup?: string
  department?: string
}

const FundingSourceNoneDropdownItem: React.FC<{ className?: string }> = (
  props,
): React.ReactNode => {
  const translate = useTranslate()

  return (
    <div
      className={props.className}
      css={css`
        ${getFontCss(
          14,
          styleConstants.colors.gray_darkest,
          styleConstants.fontFamilies.regular,
        )};
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex-grow: 2;
        height: 60px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      `}
    >
      {translate('All Accounts')}
    </div>
  )
}

interface AutomationRuleFormProps {
  onSuccessfulSubmit: (body: PutAutomationRuleRequest) => void
  isPending?: boolean
  error?: BackendErrorPayload<string>
  submitButtonContent: React.ReactNode
  initialValue?: AutomationRuleDetailModel
  showUpdates?: boolean
}

const AutomationRuleForm: React.FC<AutomationRuleFormProps> = (
  props,
): React.ReactNode => {
  const translate = useTranslate()

  const locale = useSelector(selectNullSafeLocaleFromState)
  const organizationId = useSelector(selectOrganizationIdFromRoute)
  const noUsersImageSvg = useColorOrGrayscaleSVG(svgImageNames.NoUsers)

  const baseNameAndConditions = useMemo(
    () => ({
      name: props.initialValue?.name,
      fundingSourceId: props.initialValue?.conditions?.find(
        (rc) =>
          rc.objectFieldName === 'id' &&
          rc.objectType === AutomationRuleObjectType.CreditCard,
      )?.targetValue,
      merchantName: props.initialValue?.conditions?.find(
        (rc) => rc.objectFieldName === 'merchantName',
      )?.targetValue,
      extendMccGroup: props.initialValue?.conditions?.find(
        (rc) => rc.objectFieldName === 'extendMccGroup',
      )?.targetValue,
      department: props.initialValue?.conditions?.find(
        (rc) =>
          rc.objectFieldName === 'id' &&
          rc.objectType === AutomationRuleObjectType.Department,
      )?.targetValue,
    }),
    [props.initialValue],
  )
  const baseExpCats: Record<
    string,
    Array<ExpenseDetailsModel | undefined>
  > = useMemo(
    () =>
      props.initialValue?.mappings?.reduce((acc, curr) => {
        if (curr.expenseLabel === undefined || !curr.expenseLabel.active) {
          return acc
        }

        return {
          ...acc,
          [curr.expenseCategory.id]: [
            {
              categoryId: curr.expenseCategory.id,
              categoryName: curr.expenseCategory.name,
              categoryType: curr.expenseCategory.type,
              labelId: curr.expenseLabel.id,
              labelName: curr.expenseLabel.name,
              subLabelName: curr.expenseLabel.subName,
              generatedByPrediction: false,
            } satisfies ExpenseDetailsModel,
          ],
        }
      }, {}) || {},
    [props.initialValue],
  )
  const initExpCats: Record<string, string> = useMemo(
    () =>
      props.initialValue?.mappings?.reduce((acc, curr) => {
        if (curr.expenseLabel === undefined || !curr.expenseLabel.active) {
          return acc
        }
        return {
          ...acc,
          // use labelName if no id, for free text values
          [curr.expenseCategory.id]:
            curr.expenseLabel.id || curr.expenseLabel.name,
        }
      }, {}) || {},
    [props.initialValue],
  )

  useEffect(() => {
    props.initialValue?.mappings
      ?.filter((mapping) => !mapping.expenseLabel?.active)
      .forEach((inactiveMapping) => {
        if (inactiveMapping.expenseLabel === undefined) return
        setExpenseDetailsErrors({
          ...expenseDetailsErrors,
          [inactiveMapping.expenseCategory.id]: [
            <React.Fragment
              key={`inactiveError-${inactiveMapping.expenseLabel.id}`}
            >
              {translate(
                'There was an error applying this label. Select a new label to continue.',
              )}
            </React.Fragment>,
          ],
        })
      })
  }, [props.initialValue?.mappings])

  const { data: mccData } = useQueryMccGroups({})
  // TODO - check if mccs are used outside of dropdowns - if not, move this mapping into the hook selector
  const mccOptions = useMemo(
    () =>
      mccData?.map((mcc) => ({
        value: mcc.id,
        details: {
          text: mcc.group,
        },
      })) ?? [],
    [mccData],
  )

  const [creditCardSearch, setCreditCardSearch] = useState<string | undefined>()
  const {
    data: creditCardsData,
    fetchNextPage: fetchMoreCreditCards,
    isFetching: creditCardsLoading,
    complete: creditCardsComplete,
  } = useQueryCreditCards({
    filters: {
      search: creditCardSearch,
      count: 100,
      sortField: 'type',
      sortDirection: 'ASC',
      billPayEnabled: true,
    },
  })

  const creditCardInfiniteScrollProps: CreditCardDropdownInfiniteScrollProps = {
    fetch: (filterInput, page) => {
      // TODO: this used to be handled implicitly by passing filterInput to the redux action
      // is there a cleaner way to handle this with react query?
      if (page === 0) setCreditCardSearch(filterInput)
      fetchMoreCreditCards()
    },
    page: creditCardsData?.page,
    loading: creditCardsLoading,
    complete: creditCardsComplete,
  }

  const rcnOptions = useMemo(
    () =>
      creditCardsData?.creditCards?.map((creditCard) =>
        creditCardOption(creditCard),
      ) ?? [],
    [creditCardsData?.creditCards],
  )

  // teams
  const { data: departmentsList } = useQueryDepartments({
    organizationId: organizationId,
    includeArchived: false,
  })
  const departmentOptions = useMemo(
    () =>
      departmentsList?.map((department) => ({
        value: department.id,
        details: {
          text: department.name,
        },
      })) ?? [],
    [departmentsList],
  )

  const {
    fields: rule,
    fieldsErrors: ruleErrors,
    setErrorsAndReturnDidPassValidation: validateRule,
    onFieldChange: onRuleChange,
    setFields: setRules,
    topmostScrollGroup: ruleTopmostScrollGroup,
    setTopmostScrollGroup: setRuleTopmostScrollGroup,
  } = useFormFields<AutomationRuleFormFields>(
    baseNameAndConditions,
    (): FormFieldsValidation<AutomationRuleFormFields> => {
      return {
        name: [stringIsEmpty(), stringLengthIsLessThan(255)],
        merchantName: [stringLengthIsLessThan(255)],
      }
    },
    undefined,
    undefined,
    {
      name: AutomationRuleFormInputBoxes.NameInputBox,
      fundingSourceId: AutomationRuleFormInputBoxes.RuleDefinitionInputBox,
      extendMccGroup: AutomationRuleFormInputBoxes.RuleDefinitionInputBox,
      merchantName: AutomationRuleFormInputBoxes.RuleDefinitionInputBox,
      department: AutomationRuleFormInputBoxes.RuleDefinitionInputBox,
    },
  )

  const {
    fields: expenseDetails,
    setFields: setExpenseDetails,
    fieldsErrors: expenseDetailsErrors,
    setFieldsErrors: setExpenseDetailsErrors,
    onFieldChange: onExpenseDetailChange,
    setErrorsAndReturnDidPassValidation: expenseDetailsSetErrors,
    topmostScrollGroup: expenseDetailsTopmostScrollGroup,
    setTopmostScrollGroup: setExpenseDetailsTopmostScrollGroup,
  } = useFormFields<Record<string, string>>(
    initExpCats,
    (): FormFieldsValidation<Record<string, string>> =>
      ({}) as FormFieldsValidation<Record<string, string>>,
    undefined,
    undefined,
    AutomationRuleFormInputBoxes.RuleDefinitionInputBox,
  )

  const inputBoxRefs = useKeyedDivRefs(AutomationRuleFormInputBoxes)
  useScrollToScrollGroupRefs(
    inputBoxRefs ? Object.values(inputBoxRefs) : [],
    [ruleTopmostScrollGroup, expenseDetailsTopmostScrollGroup],
    [setRuleTopmostScrollGroup, setExpenseDetailsTopmostScrollGroup],
  )

  const topLevelError = useMemo(
    () =>
      props.error ? parseBackendErrors(props.error).topLevelError : undefined,
    [props.error],
  )

  const onSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault()

    const flatFieldsPassedValidation = validateRule()
    const expenseDetailsPassedValidation = expenseDetailsSetErrors()
    if (flatFieldsPassedValidation && expenseDetailsPassedValidation) {
      props.onSuccessfulSubmit({
        name: rule.name || '', // won't actually ever be undefined due to validation checks
        status: props.initialValue?.status ?? AutomationRuleStatusType.Active,
        conditions: [
          ...(rule.fundingSourceId !== undefined
            ? [
                {
                  objectType: AutomationRuleObjectType.CreditCard,
                  objectFieldName: 'id',
                  comparator: AutomationComparator.Equals,
                  targetValue: rule.fundingSourceId,
                  group: 0,
                },
              ]
            : []),
          ...(rule.extendMccGroup !== undefined
            ? [
                {
                  objectType: AutomationRuleObjectType.Transaction,
                  objectFieldName: 'extendMccGroup',
                  comparator: AutomationComparator.Equals,
                  targetValue: rule.extendMccGroup,
                  group: 0,
                },
              ]
            : []),
          ...(rule.merchantName !== undefined && rule.merchantName !== ''
            ? [
                {
                  objectType: AutomationRuleObjectType.Transaction,
                  objectFieldName: 'merchantName',
                  comparator: AutomationComparator.Contains,
                  targetValue: rule.merchantName,
                  group: 0,
                },
              ]
            : []),
          ...(rule.department !== undefined && rule.department !== ''
            ? [
                {
                  objectType: AutomationRuleObjectType.Department,
                  objectFieldName: 'id',
                  comparator: AutomationComparator.Equals,
                  targetValue: rule.department,
                  group: 0,
                },
              ]
            : []),
        ],
        mappings:
          expenseDetails !== undefined
            ? Object.entries(expenseDetails)
                .filter((keyVal) => !!keyVal[1])
                .map(([key, val]) => ({ categoryId: key, labelId: val }))
            : [],
      })
    }
  }

  return (
    <form
      onSubmit={onSubmit}
      css={css`
        display: flex;
        flex-direction: column;
        gap: 20px;
      `}
    >
      <Box
        className={'ruleName'}
        css={css`
          display: flex;
          flex-direction: column;
        `}
        refObj={inputBoxRefs?.[AutomationRuleFormInputBoxes.NameInputBox]}
      >
        <TextInputField
          id={'nameInput'}
          fieldLabel={translate('Custom Rule Name')}
          baseValue={baseNameAndConditions.name}
          value={rule.name}
          errors={ruleErrors.name}
          onChange={(name: string): void => {
            onRuleChange('name', name)
          }}
          placeholder={translateSingleString(locale, 'Enter a name')}
          design={TextInputFieldDesigns.New}
          characterLimit={255}
          hideCharacterLimitCount
          showUpdates={props.showUpdates}
          hideWasOnUpdate
        />
      </Box>
      <Box
        className={'ruleAttributes'}
        css={css`
          display: flex;
          flex-direction: column;
          gap: 24px;
        `}
        refObj={
          inputBoxRefs?.[AutomationRuleFormInputBoxes.RuleDefinitionInputBox]
        }
      >
        <div
          css={css`
            ${getFontCss(
              16,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.medium,
            )};
          `}
        >
          {translate('For transactions that contain')}
        </div>
        <CreditCardDropdown
          id={'fundingSourceDropdown'}
          fieldLabel={translate('Funding Source')}
          placeholder={translateSingleString(locale, 'All Accounts')}
          errors={ruleErrors.fundingSourceId}
          baseValues={
            baseNameAndConditions.fundingSourceId !== undefined
              ? new Set([baseNameAndConditions.fundingSourceId])
              : new Set<string>()
          }
          selectedValues={
            rule.fundingSourceId !== undefined
              ? new Set([rule.fundingSourceId])
              : new Set<string>()
          }
          chooseOption={(fundingSourceId: string): void => {
            onRuleChange('fundingSourceId', fundingSourceId)
          }}
          optionGroups={[{ options: rcnOptions }]}
          isMulti={false}
          infiniteScrollProps={creditCardInfiniteScrollProps}
          allowNoneProps={{
            NoneDropdownItem: FundingSourceNoneDropdownItem,
            deselectAllOptions: (): void => {
              const newRules = _.cloneDeep(rule)
              delete newRules['fundingSourceId']
              setRules(newRules)
            },
          }}
          showUpdates={props.showUpdates}
          hideWasOnUpdate
          tooltip={
            <Tooltip
              iconTestId={'fundingSourceTooltip'}
              openLeft={false}
              css={css`
                z-index: 1;
                padding-bottom: 8px;
              `}
            >
              <p>
                {translate(
                  '${theFundingSource} list will only contain accounts and budgets.',
                  {
                    theFundingSource: (
                      <span
                        css={css`
                          ${getFontCss(
                            12,
                            themeConstants.colors.font_primary,
                            styleConstants.fontFamilies.medium,
                          )};
                        `}
                      >
                        {translate('The Funding Source')}
                      </span>
                    ),
                  },
                )}
              </p>
            </Tooltip>
          }
        />
        <TextDropdown
          id={'departmentDropdown'}
          fieldLabel={translate('Team')}
          placeholder={translateSingleString(
            locale,
            'Type or select from list',
          )}
          isOptional
          optionGroups={[{ options: departmentOptions }]}
          errors={ruleErrors.department}
          baseValues={
            baseNameAndConditions.department !== undefined
              ? new Set([baseNameAndConditions.department])
              : new Set<string>()
          }
          selectedValues={
            rule.department !== undefined
              ? new Set([rule.department])
              : new Set<string>()
          }
          chooseOption={(department: string): void => {
            onRuleChange('department', department)
          }}
          deselectAllOptions={
            departmentOptions.length !== 0
              ? (): void => {
                  const newRules = _.cloneDeep(rule)
                  delete newRules['department']
                  setRules(newRules)
                }
              : undefined
          }
          showUpdates={props.showUpdates}
          hideWasOnUpdate
          customEmptyDropdownMessage={
            <EmptyDropdownMessage
              imageSvg={noUsersImageSvg}
              title={translate('No teams have been created')}
              subtitle={translate(
                'Looks like there are no teams to choose from. You can create teams from the "Teams" tab in Organization Settings.',
              )}
            />
          }
        />
        <TextDropdown
          id={'mccDropdown'}
          fieldLabel={translate('Merchant Category')}
          placeholder={translateSingleString(
            locale,
            'Type or select from list',
          )}
          isOptional
          optionGroups={[{ options: mccOptions }]}
          errors={ruleErrors.extendMccGroup}
          baseValues={
            baseNameAndConditions.extendMccGroup !== undefined
              ? new Set([baseNameAndConditions.extendMccGroup])
              : new Set<string>()
          }
          selectedValues={
            rule.extendMccGroup !== undefined
              ? new Set([rule.extendMccGroup])
              : new Set<string>()
          }
          chooseOption={(mccGroup: string): void => {
            onRuleChange('extendMccGroup', mccGroup)
          }}
          deselectAllOptions={(): void => {
            const newRules = _.cloneDeep(rule)
            delete newRules['extendMccGroup']
            setRules(newRules)
          }}
          showUpdates={props.showUpdates}
          hideWasOnUpdate
        />
        <TextInputField
          id={'merchantNameInput'}
          fieldLabel={translate('Merchant Name Contains')}
          isOptional
          baseValue={baseNameAndConditions.merchantName}
          value={rule.merchantName}
          errors={ruleErrors.merchantName}
          onChange={(merchantName: string): void => {
            onRuleChange('merchantName', merchantName)
          }}
          placeholder={translateSingleString(locale, 'Enter a merchant name')}
          design={TextInputFieldDesigns.New}
          characterLimit={255}
          hideCharacterLimitCount
          showUpdates={props.showUpdates}
          hideWasOnUpdate
          tooltip={
            <Tooltip
              iconTestId={'merchantNameTooltip'}
              openLeft={false}
              css={css`
                z-index: 1;
              `}
            >
              {translate(
                'Type a name (e.g., “starbucks”), and the rule will match any merchants that contains it (e.g., “starbucks coffee”).',
              )}
            </Tooltip>
          }
        />
        <SvgArrowCircle
          css={css`
            align-self: center;
          `}
        />
        <div
          css={css`
            gap: 12px;
          `}
        >
          <div
            css={css`
              ${getFontCss(
                16,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.medium,
              )};
              margin-bottom: 12px;
            `}
          >
            {translate('Map to these categories')}
          </div>
          <div
            css={css`
              ${getFontCss(
                14,
                styleConstants.colors.gray_darkest,
                styleConstants.fontFamilies.regular,
              )};
            `}
          >
            {translate(
              'Categories you select here will be automatically applied to cleared transactions that match your rule conditions',
            )}
          </div>
        </div>
        <ExpenseCategoriesInputs
          context={ExpenseCategoriesContext.TRANSACTION}
          expenseDetails={expenseDetails}
          expenseDetailsErrors={expenseDetailsErrors}
          initialExpDetailsMultiple={baseExpCats}
          onExpenseCategoryChange={(
            categoryId: string,
            labelId: string,
          ): void => {
            onExpenseDetailChange(categoryId, labelId)
          }}
          deselectExpenseCategory={(categoryId: string): void => {
            const newExpenseCategories = { ...expenseDetails }
            delete newExpenseCategories[categoryId]
            setExpenseDetails(newExpenseCategories)
          }}
          showUpdates={props.showUpdates}
          hideWasOnUpdate
        />
      </Box>
      {topLevelError !== undefined && (
        <span
          css={css`
            margin-top: 40px;
            ${getFontCss(
              14,
              styleConstants.colors.red,
              styleConstants.fontFamilies.regular,
            )};

            ${getBreakpointDown(breakpointSizes.XSM)(css`
              margin-top: 30px;
            `)};
          `}
        >
          {topLevelError}
        </span>
      )}
      <div>
        <Button
          id={'createAutomationRuleBtn'}
          testId={'createAutomationRuleBtn'}
          design={ButtonDesigns.designCta}
          type={ButtonTypes.submit}
          big={true}
          text={props.submitButtonContent}
          loading={props.isPending}
          disabled={props.isPending}
          css={css`
            display: flex;
            margin-top: 12px;
          `}
        />
      </div>
    </form>
  )
}

export default AutomationRuleForm
